import React, { useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  Button,
  Platform,
  TextProps,
} from "react-native";
import { useTranslation } from "react-i18next";
import { TeamworksWordmark } from "../components/TeamworksLogo";
import { Storage } from "../helpers/storage";
import { defer } from "lodash";
import { reloadAsync } from "expo-updates";
import { Spacer } from "../components/ViewComponents";
import { BackgroundGradient } from "../components/BackgroundGradient";
import { ScreenFooter } from "../components/ScreenFooter";
import { useIpAddress } from "../hooks/useIpAddress";
import { useAppReset } from "../providers/AppResetProvider";
import { config } from "../config";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    page: {
      flex: 1,
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    errorTitle: {
      fontWeight: "bold",
      fontSize: theme.fontSize.h4,
      marginVertical: theme.spacing.medium,
      color: theme.colors.black,
    },
    errorMessage: {
      lineHeight: theme.fontSize.medium,
      fontSize: theme.fontSize.detail,
      color: theme.colors.grey1,
    },
    container: {
      backgroundColor: theme.colors.white,
      alignItems: "flex-start",
      justifyContent: "center",
      padding: theme.spacing.large,
      width: "70%",
      maxWidth: 800,
      borderRadius: theme.borderRadius.medium,
    },
  });

export const ErrorScreen = (props: {
  message?: string;
  showResetButton?: boolean;
  subMessages?: Array<string>;
  showIpAddress?: boolean;
}) => {
  const { t } = useTranslation("ErrorScreen");
  const theme = useTheme();
  const styles = makeStyles(theme);
  const appReset = useAppReset();

  // if there's any additional context, do not attempt automatic reset
  const shouldAttemptReset = useMemo(() => {
    return !(
      props.message != null ||
      props.subMessages != null ||
      props.showResetButton ||
      props.showIpAddress
    );
  }, [props]);

  useEffect(() => {
    if (!shouldAttemptReset) return;

    const timeout = setTimeout(
      appReset.reset,
      config.errorScreenResetDelay * 1000,
    );
    return () => clearTimeout(timeout);
  }, [shouldAttemptReset, appReset]);

  return (
    <>
      <View style={styles.page}>
        <BackgroundGradient />
        <View style={styles.container}>
          <TeamworksWordmark style={{ height: theme.iconSize.large }} />
          <Spacer size="medium" />
          <Text style={styles.errorTitle}>
            {props.message ?? t("Something went wrong")}
          </Text>
          <Spacer size="small" />
          <View>
            {(
              props.subMessages ?? [
                t(
                  "Sorry for the inconvience, our engineers have been notified and will be working on a fix",
                ),
                t(
                  "If this issue persists, please contact your account manager",
                ),
              ]
            ).map((msg, index) => (
              <Text
                key={index}
                style={[
                  styles.errorMessage,
                  { marginBottom: theme.spacing.small },
                ]}
              >
                {msg}
              </Text>
            ))}
            {props.showIpAddress && (
              <IpAddressText style={styles.errorMessage} />
            )}
          </View>

          {props.showResetButton && (
            <>
              <Spacer size="large" />
              <Button
                title={t("Reset device")}
                onPress={async () => {
                  await Storage.clear();
                  defer(() => {
                    if (Platform.OS === "web") {
                      window.location.reload();
                    } else {
                      reloadAsync();
                    }
                  });
                }}
              />
            </>
          )}

          {shouldAttemptReset && (
            <>
              <Spacer size="medium" />
              <Text style={styles.errorMessage}>
                {t(
                  "The app will attempt to automatically reset in {{seconds}} seconds",
                  { seconds: config.errorScreenResetDelay },
                )}
              </Text>
            </>
          )}
        </View>
      </View>
      <ScreenFooter />
    </>
  );
};

const IpAddressText = (props: TextProps) => {
  const ip = useIpAddress();
  const { t } = useTranslation("ErrorScreen");
  return (
    <Text {...props}>
      {t("Your IP address may need to be permitted: {{ip}}", { ip })}
    </Text>
  );
};
